/**
 * NeedToStockDialog — Modale Admin pour passer directement un besoin en Stock.
 *
 * Pour chaque item du besoin :
 *   • Mapping obligatoire vers un produit stock existant (dropdown recherche)
 *   • Quantité (défaut = qty du besoin)
 *   • Prix unitaire (défaut = celui du besoin, éditable)
 * + Champs globaux : fournisseur, date d'achat, notes
 * Appelle POST /api/needs/{id}/convert-to-stock.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, Search, Truck, Calendar, X, Check, Loader2 } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function NeedToStockDialog({ open, onClose, need, actorName = "admin", onSuccess }) {
  const [stockProducts, setStockProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [supplierName, setSupplierName] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(todayISO());
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Charger la liste des produits stock au 1er open
  useEffect(() => {
    if (!open) return;
    setLoadingProducts(true);
    axios.get(`${API}/stock/products`)
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.products || []);
        setStockProducts(list);
      })
      .catch(() => setStockProducts([]))
      .finally(() => setLoadingProducts(false));
  }, [open]);

  // Fonction de matching intelligent : cherche le meilleur produit stock pour une description
  const autoMatch = (description, products) => {
    if (!description || !products || products.length === 0) return null;
    const desc = description.trim().toLowerCase();
    if (!desc) return null;

    // Normalisation : retire accents, ponctuation, articles courants
    const normalize = (s) => (s || "")
      .toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[.,;:!?()"']/g, " ")
      .replace(/\b(de|du|des|le|la|les|un|une|d|l|au|aux)\b/g, " ")
      .replace(/\s+/g, " ").trim();

    const descN = normalize(desc);
    if (!descN) return null;

    // 1) Match exact (nom identique)
    let best = products.find(p => normalize(p.name) === descN);
    if (best) return best;

    // 2) Le nom du produit est contenu dans la description (ex: "5kg de tomates" → "tomates")
    //    ou inversement (description courte incluse dans nom produit)
    const candidates = [];
    for (const p of products) {
      const pn = normalize(p.name);
      if (!pn) continue;
      if (descN.includes(pn) || pn.includes(descN)) {
        // Score = longueur commune / longueur max (plus c'est proche, mieux c'est)
        const score = Math.min(pn.length, descN.length) / Math.max(pn.length, descN.length);
        candidates.push({ p, score });
      }
    }
    if (candidates.length > 0) {
      candidates.sort((a, b) => b.score - a.score);
      return candidates[0].p;
    }

    // 3) Match par mots-clés (au moins un mot significatif > 3 chars en commun)
    const descWords = descN.split(" ").filter(w => w.length > 3);
    if (descWords.length === 0) return null;
    let bestKw = null, bestScore = 0;
    for (const p of products) {
      const pn = normalize(p.name);
      const pWords = pn.split(" ").filter(w => w.length > 3);
      const common = descWords.filter(w => pWords.includes(w)).length;
      if (common > bestScore) { bestScore = common; bestKw = p; }
    }
    return bestKw;
  };

  // Initialiser les lignes à partir des items du besoin
  useEffect(() => {
    if (!open || !need) return;
    const src = need.items && need.items.length > 0
      ? need.items
      : [{ description: need.description, quantity: need.quantity || 1, unit_price: need.unit_price || 0 }];
    setRows(src.map((it, idx) => {
      const desc = it.description || "";
      const matched = autoMatch(desc, stockProducts);
      return {
        key: `${need.id}-${idx}`,
        description_source: desc,
        quantity: Number(it.quantity || 1),
        unit_price: Number(it.unit_price || 0),
        stock_product_id: matched ? matched.id : "",
        _query: matched ? matched.name : desc,
        _openList: false,
        _autoMatched: !!matched,
      };
    }));
    setSupplierName((need.supplier || "").trim());
    setPurchaseDate(todayISO());
    setNotes("");
  }, [open, need, stockProducts]);

  const totalAmount = useMemo(
    () => rows.reduce((s, r) => s + (r.quantity || 0) * (r.unit_price || 0), 0),
    [rows]
  );

  const updateRow = (idx, patch) => {
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const getMatches = (query) => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return stockProducts.slice(0, 8);
    return stockProducts.filter(p =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.code || "").toLowerCase().includes(q)
    ).slice(0, 12);
  };

  const validate = () => {
    const missing = rows.filter(r => !r.stock_product_id);
    if (missing.length > 0) {
      toast.error(`${missing.length} article(s) sans produit stock associé`);
      return false;
    }
    const zeros = rows.filter(r => !r.quantity || r.quantity <= 0);
    if (zeros.length > 0) {
      toast.error(`${zeros.length} article(s) sans quantité`);
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        mappings: rows.map(r => ({
          stock_product_id: r.stock_product_id,
          quantity: r.quantity,
          unit_price: r.unit_price,
          description_source: r.description_source,
        })),
        supplier_name: supplierName.trim() || "Besoin interne",
        purchase_date: purchaseDate,
        user_name: actorName,
        notes: notes.trim(),
      };
      const res = await axios.post(`${API}/needs/${need.id}/convert-to-stock`, payload);
      if (res.data?.success) {
        toast.success(`Stock mis à jour : ${res.data.items_added} article(s)`);
        onSuccess?.();
        onClose?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du passage en stock");
    } finally {
      setSubmitting(false);
    }
  };

  if (!need) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl bg-slate-900 border-emerald-500/30 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-300 text-lg">
            <Package className="w-5 h-5" />
            Passer le besoin directement en Stock
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Chaque article doit être associé à un produit Stock existant. Le stock sera incrémenté immédiatement.
          </DialogDescription>
        </DialogHeader>

        {loadingProducts ? (
          <div className="py-10 text-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Chargement du catalogue Stock…
          </div>
        ) : (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            {/* Champs globaux */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <div>
                <Label className="text-slate-300 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1">
                  <Truck className="w-3 h-3" /> Fournisseur
                </Label>
                <Input
                  value={supplierName}
                  onChange={(e) => setSupplierName(e.target.value)}
                  placeholder="Ex: Marché Dantokpa"
                  className="bg-slate-900/70 border-slate-600 text-white mt-1"
                  data-testid="need-to-stock-supplier"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Date d&apos;achat
                </Label>
                <Input
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  className="bg-slate-900/70 border-slate-600 text-white mt-1"
                  data-testid="need-to-stock-date"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-[11px] uppercase tracking-wider font-bold">Notes</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Commentaire (optionnel)"
                  className="bg-slate-900/70 border-slate-600 text-white mt-1"
                />
              </div>
            </div>

            {/* Table articles */}
            <div className="rounded-lg border border-slate-700 overflow-hidden">
              {rows.length > 0 && (() => {
                const autoCount = rows.filter(r => r._autoMatched && r.stock_product_id).length;
                if (autoCount === 0) return null;
                return (
                  <div className="px-3 py-1.5 bg-emerald-950/30 text-emerald-300 text-[11px] font-semibold border-b border-emerald-500/20">
                    ⚡ Matching auto : {autoCount}/{rows.length} article(s) pré-remplis. Vérifiez et ajustez si besoin.
                  </div>
                );
              })()}
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-800/80 text-slate-300 text-[10.5px] uppercase tracking-wider font-bold">
                <div className="col-span-5">Article du besoin → Produit stock</div>
                <div className="col-span-2 text-right">Qté</div>
                <div className="col-span-2 text-right">PU (F)</div>
                <div className="col-span-3 text-right">Total</div>
              </div>
              {rows.map((r, idx) => {
                const matched = stockProducts.find(p => p.id === r.stock_product_id);
                return (
                  <div key={r.key} className="grid grid-cols-12 gap-2 px-3 py-2.5 border-t border-slate-800 items-start">
                    <div className="col-span-5">
                      <p className="text-slate-500 text-[10.5px] mb-1 truncate italic">« {r.description_source} »</p>
                      <div className="relative">
                        <div className="relative">
                          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                          <Input
                            value={r._query}
                            onChange={(e) => updateRow(idx, { _query: e.target.value, _openList: true, stock_product_id: "" })}
                            onFocus={() => updateRow(idx, { _openList: true })}
                            placeholder="Chercher un produit stock…"
                            className={`h-8 pl-7 text-[12.5px] bg-slate-900/80 border ${matched ? 'border-emerald-500/60' : 'border-slate-600'} text-white`}
                            data-testid={`need-to-stock-search-${idx}`}
                          />
                          {matched && (
                            <Check className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-emerald-400" />
                          )}
                        </div>
                        {r._openList && !matched && (
                          <div className="absolute z-50 top-full mt-1 left-0 right-0 max-h-56 overflow-y-auto rounded-lg border border-slate-600 bg-slate-950 shadow-xl">
                            {getMatches(r._query).length === 0 ? (
                              <p className="p-3 text-slate-500 text-[11.5px] italic text-center">Aucun produit trouvé</p>
                            ) : getMatches(r._query).map(p => (
                              <button
                                key={p.id}
                                type="button"
                                onClick={() => updateRow(idx, {
                                  stock_product_id: p.id,
                                  _query: p.name,
                                  _openList: false,
                                  _autoMatched: false,
                                })}
                                className="w-full text-left px-3 py-2 hover:bg-emerald-500/15 border-b border-slate-800 last:border-0"
                                data-testid={`need-to-stock-pick-${idx}-${p.id}`}
                              >
                                <p className="text-white text-[12.5px] font-semibold truncate">{p.name}</p>
                                <p className="text-slate-500 text-[10.5px]">
                                  Stock actuel : {p.quantity || 0} {p.unit || ""} · {p.code || "—"}
                                </p>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {matched && (
                        <p className="text-emerald-400 text-[10.5px] mt-1 flex items-center gap-1.5">
                          <span>→ {matched.name} <span className="text-slate-500">(stock : {matched.quantity || 0} {matched.unit || ""})</span></span>
                          {r._autoMatched && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" title="Correspondance automatique">
                              Auto
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number" min="0" step="any"
                        value={r.quantity || ""}
                        onChange={(e) => updateRow(idx, { quantity: parseFloat(e.target.value) || 0 })}
                        className="h-8 text-right bg-slate-900/70 border-slate-600 text-white"
                        data-testid={`need-to-stock-qty-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number" min="0" step="any"
                        value={r.unit_price || ""}
                        onChange={(e) => updateRow(idx, { unit_price: parseFloat(e.target.value) || 0 })}
                        placeholder="0"
                        className="h-8 text-right bg-slate-900/70 border-slate-600 text-white"
                      />
                    </div>
                    <div className="col-span-3 flex items-center justify-end pt-1.5">
                      <span className="text-emerald-300 font-mono text-[13px] font-bold">
                        {new Intl.NumberFormat("fr-FR").format(Math.round((r.quantity || 0) * (r.unit_price || 0)))} F
                      </span>
                    </div>
                  </div>
                );
              })}
              <div className="grid grid-cols-12 gap-2 px-3 py-2.5 bg-emerald-950/40 border-t-2 border-emerald-500/30">
                <div className="col-span-9 text-slate-200 text-[12.5px] font-bold uppercase tracking-wide">Total</div>
                <div className="col-span-3 text-right text-emerald-300 font-mono text-[15px] font-black" data-testid="need-to-stock-total">
                  {new Intl.NumberFormat("fr-FR").format(Math.round(totalAmount))} F
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
              <Button type="button" variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">
                <X className="w-4 h-4 mr-1" /> Annuler
              </Button>
              <Button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="need-to-stock-submit"
              >
                {submitting
                  ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Enregistrement…</>
                  : <><Check className="w-4 h-4 mr-1" /> Valider et passer en stock</>
                }
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
